/**
 * PATH: packages/ui/src/components/sidebar/HubLinkWithFlyout.tsx
 * PURPOSE: Hub link in sidebar with hover flyout showing hub pages
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { ChevronRight, ExternalLink as ExternalLinkIcon } from 'lucide-react'
import { cn } from '../../lib/utils'
import { navShortLabel } from './navShortLabel'
import { COLLAPSED_NAV_DOCK_TILE } from './NavItemWithFlyout'
import type { HubLinkWithFlyoutProps } from './types'

export function HubLinkWithFlyout({ link, collapsed, activeHubId, setActiveHubId, lastSidebarScrollAtRef, onHubClick, currentHubId }: HubLinkWithFlyoutProps) {
  const [flyoutPosition, setFlyoutPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const flyoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const linkId = link.id || link.label
  const hasPages = (link.pages?.length ?? 0) > 0
  const showFlyout = activeHubId === linkId
  const isCurrentHub = currentHubId === linkId

  const handleMouseEnter = useCallback(() => {
    if (Date.now() - lastSidebarScrollAtRef.current < 150) return
    if (flyoutTimeoutRef.current) clearTimeout(flyoutTimeoutRef.current)
    if (containerRef.current && (collapsed || hasPages)) {
      const rect = containerRef.current.getBoundingClientRect()
      const flyoutHeight = Math.min(350, (link.pages?.length || 0) * 40 + 120)
      const viewportHeight = window.innerHeight
      const wouldOverflowBottom = rect.top + flyoutHeight > viewportHeight - 16
      setFlyoutPosition({
        top: wouldOverflowBottom ? Math.max(16, viewportHeight - flyoutHeight - 16) : rect.top,
        left: rect.right + 8,
      })
      setActiveHubId(linkId)
    }
  }, [hasPages, lastSidebarScrollAtRef, linkId, setActiveHubId, link.pages?.length, collapsed])

  const handleMouseLeave = useCallback(() => {
    flyoutTimeoutRef.current = setTimeout(() => {
      setActiveHubId((current) => (current === linkId ? null : current))
    }, 250)
  }, [linkId, setActiveHubId])

  useEffect(() => { return () => { if (flyoutTimeoutRef.current) clearTimeout(flyoutTimeoutRef.current) } }, [])

  // Only intercept planned links and onHubClick — let browser handle normal <a> navigation
  const handleLinkClick = useCallback((e: React.MouseEvent) => {
    if (link.status === 'planned') { e.preventDefault(); return }
    if (onHubClick && link.id) { e.preventDefault(); onHubClick(link.id, link.href) }
    // No preventDefault — browser follows <a href> naturally (same tab, no target="_blank")
  }, [onHubClick, link.id, link.href, link.status])

  const statusClasses = link.status === 'planned'
    ? "text-muted-foreground hover:text-muted-foreground hover:bg-muted"
    : link.status === 'development'
      ? "text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20"
      : isCurrentHub
        ? "text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/30 font-medium"
        : "text-muted-foreground hover:text-foreground hover:bg-muted"

  const flyoutContent = showFlyout && (
    <FlyoutPortal position={flyoutPosition} link={link} hasPages={hasPages} collapsed={collapsed}
      onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onHubClick={onHubClick} />
  )

  if (collapsed) {
    // BUG-2026-05-23-COLLAPSED-HUB-ICONS-NO-LABEL — the hubs section
    // at the bottom of the collapsed sidebar previously rendered as
    // pure icons (`<link.icon className="w-4 h-4" />` only) while the
    // main nav above it rendered as stacked icon+label tiles via
    // `COLLAPSED_NAV_DOCK_TILE` (see NavItemWithFlyout). Operators
    // saw an inconsistent rail: Dashboard / Active Jobs / Reports
    // / Emails had names visible, the hub-switcher icons below did
    // not. First-principles fix: render BOTH sections with the same
    // stacked tile shape so the rail reads as one consistent
    // top-to-bottom hub strip — same visual language as the
    // MobileDock at the bottom of mobile viewports.
    const shortLabel = navShortLabel(link.label)
    return (
      <div ref={containerRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <a
          href={link.href}
          onClick={handleLinkClick}
          title={link.label}
          aria-label={link.label}
          data-hub-collapsed-tile=""
          className={cn(
            COLLAPSED_NAV_DOCK_TILE,
            link.status === 'planned' ? "text-muted-foreground hover:text-muted-foreground hover:bg-muted"
            : link.status === 'development' ? "text-amber-500 dark:text-amber-500 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
            : isCurrentHub ? "text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/30"
            : "text-muted-foreground hover:text-teal-600 dark:hover:text-teal-400 hover:bg-muted",
            showFlyout && !isCurrentHub && "bg-muted",
          )}
        >
          <link.icon className="h-5 w-5 shrink-0" />
          <span className="max-w-full truncate text-[10px] font-medium leading-tight">{shortLabel}</span>
          {link.status === 'development' && (
            <span aria-hidden="true" className="absolute -top-0.5 -right-0.5 inline-flex h-2 w-2 rounded-full bg-amber-400" title="In development" />
          )}
          {link.status === 'planned' && (
            <span aria-hidden="true" className="absolute -top-0.5 -right-0.5 inline-flex h-2 w-2 rounded-full bg-slate-400" title="Planned" />
          )}
        </a>
        {flyoutContent}
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <a href={link.href} onClick={handleLinkClick}
        className={cn("flex items-center gap-2 px-2.5 py-1.5 text-[12px] rounded-md transition-colors", statusClasses)}>
        <link.icon className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="truncate flex-1">{link.label}</span>
        {link.status === 'development' && <span className="px-1.5 py-0.5 text-[9px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 rounded-full flex-shrink-0">DEV</span>}
        {link.status === 'planned' && <span className="px-1.5 py-0.5 text-[9px] font-medium bg-muted text-muted-foreground rounded-full flex-shrink-0">SOON</span>}
        {hasPages && <ChevronRight className="w-3 h-3 text-muted-foreground/50 flex-shrink-0" />}
      </a>
      {hasPages && flyoutContent}
    </div>
  )
}

function FlyoutPortal({ position, link, hasPages, collapsed, onMouseEnter, onMouseLeave, onHubClick }: {
  position: { top: number; left: number }; link: any; hasPages: boolean; collapsed: boolean
  onMouseEnter: () => void; onMouseLeave: () => void; onHubClick?: (hubId: string, href: string) => void
}) {
  return createPortal(
    <div className="fixed bg-popover border border-border rounded-xl shadow-xl z-[9999] py-1.5 animate-in slide-in-from-left-2 duration-150 w-56"
      style={{ top: position.top, left: position.left }} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div className={cn("px-3 pb-2 mb-1", hasPages && "border-b border-border")}>
        <div className="flex items-center gap-2">
          <div className={cn("w-6 h-6 rounded-lg flex items-center justify-center", link.status === 'development' ? "bg-amber-100 dark:bg-amber-900/30" : "bg-muted")}>
            <link.icon className={cn("w-3.5 h-3.5", link.status === 'development' ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground")} />
          </div>
          <div className="flex-1">
            <p className="text-[12px] font-semibold text-foreground">{link.label}</p>
            {link.description && <p className="text-[10px] text-muted-foreground line-clamp-1">{link.description}</p>}
          </div>
          {link.status === 'development' && <span className="px-1 py-0.5 text-[8px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 rounded">DEV</span>}
          {link.status === 'planned' && <span className="px-1 py-0.5 text-[8px] font-medium bg-muted text-muted-foreground rounded">SOON</span>}
        </div>
      </div>
      {hasPages && (
        <div className="max-h-64 overflow-y-auto">
          <p className="px-3 py-1 text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Pages</p>
          {link.pages!.map((page: any, idx: number) => {
            const PageIcon = page.icon || ExternalLinkIcon
            return (
              <a key={idx} href={page.href}
                onClick={(e) => {
                  // Intercept consolidated hub page links for SPA navigation
                  if (onHubClick && link.id) { e.preventDefault(); onHubClick(link.id, page.href) }
                }}
                className="flex items-center gap-2.5 px-3 py-2 text-[12px] text-muted-foreground hover:bg-muted transition-colors mx-1 rounded-md">
                <PageIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /><span className="truncate">{page.label}</span>
              </a>
            )
          })}
        </div>
      )}
      <div className={cn("px-3 pt-2", hasPages && "mt-1 border-t border-border")}>
        <a href={link.href}
          onClick={(e) => { if (onHubClick && link.id) { e.preventDefault(); onHubClick(link.id, link.href) } }}
          className="flex items-center gap-2 text-[11px] text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300">
          <span>Open {link.label}</span>
        </a>
      </div>
    </div>,
    document.body
  )
}
